import React, { useState, useEffect } from 'react';
import { alpha } from '@mui/material/styles';
import { FilterItemDto } from 'models/dtoTypes';
import { IFilterField } from 'models/types';
import { styled } from '@mui/material/styles';
import { get } from 'lodash';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Pagination from '@mui/material/Pagination';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Link from 'components/Link';
import FilterPanel from 'components/FilterPanel';
import { useTranslation } from 'react-i18next';

interface EnhancedTableToolbarProps {
  selectIds: string[];
  numSelected: number;
  batchActions: IBatchAction[];
}

function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
  const { selectIds, numSelected, batchActions } = props;
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('default');
  const processBatchAction = async (action: IBatchAction) => {
    const { onClick } = action;
    setLoading(true);
    await onClick(selectIds);
    setLoading(false);
  };

  const renderTitle = () => {
    if (loading) {
      return (
        <Stack direction="row" spacing={1}>
          <CircularProgress size={25} />
          <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1" component="div">
            Processing {numSelected} item{numSelected > 0 ? 's' : ''}
          </Typography>
        </Stack>
      );
    } else if (numSelected > 0) {
      return (
        <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1" component="div">
          {numSelected} selected
        </Typography>
      );
    } else {
      return (
        <Typography sx={{ flex: '1 1 100%' }} variant="h6" id="tableTitle" component="div">
          {t('multiple-select-actions')}
        </Typography>
      );
    }
  };

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
      }}
    >
      {renderTitle()}
      {!loading && numSelected > 0 ? (
        <Stack direction="row" spacing="1">
          {batchActions.map((action, index) => {
            const { icon, tooltip } = action;
            return (
              <Tooltip title={tooltip} key={index}>
                <IconButton onClick={() => processBatchAction(action)}>{icon}</IconButton>
              </Tooltip>
            );
          })}
        </Stack>
      ) : null}
    </Toolbar>
  );
}

const PREFIX = 'SelectableResourceTable';
const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    color: 'black',
    fontWeight: 'bold',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
    // borderRadius: 5,
    borderTop: '1px solid',
    borderBottom: '1px solid',
    '&:first-of-type': {
      borderLeft: '1px solid',
      borderTopLeftRadius: 5,
      borderBottomLeftRadius: 5,
    },
    '&:last-of-type': {
      borderRight: '1px solid',
      borderTopRightRadius: 5,
      borderBottomRightRadius: 5,
      // transform: 'scale(1.05)',
    },
  },
}));

const classes = {
  root: `${PREFIX}-root`,
  actions: `${PREFIX}-actions`,
  pagination: `${PREFIX}-pagination`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  [`& .${classes.pagination}`]: {
    paddingTop: 10,
    paddingBottom: 10,
  },
}));

export interface IBatchAction {
  icon: JSX.Element;
  tooltip: string;
  onClick: (ds: string[]) => Promise<void>;
}

export type IActionFn<TAttributes> = (model: TAttributes) => Promise<TAttributes | undefined>;

export interface ITableRowAction<TAttributes> {
  label: string;
  onClick: IActionFn<TAttributes>;
  hide?: (model: TAttributes) => boolean;
}

export interface IColumn<TAttributes> {
  label: string;
  dataIndex?: string;
  sortIndex?: string;
  actions?: ITableRowAction<TAttributes>[];
  render?: (model: TAttributes) => JSX.Element | string;
}

type ResourceWithCount<TAttributes> = {
  pageCount?: number;
  count?: number;
  page?: number;
  items?: TAttributes[] | null;
};

interface IProps<TAttributes> {
  filterSchema?: IFilterField[];
  columns: IColumn<TAttributes>[];
  onFetch: (options: {
    pageIndex: number;
    organisationId?: string;
  }) => Promise<ResourceWithCount<TAttributes>>;
  defaultQuery?: FilterItemDto[];
  defaultSort?: { sortColumn: string; isDesc: boolean };
  batchActions: IBatchAction[];
}

interface IFilters {
  [key: string]: string;
}

function SingleItem<TAttributes>(props: {
  row: TAttributes;
  columns: IColumn<TAttributes>[];
  selected: boolean;
  onToggleSelectRow: () => void;
}) {
  const { row, columns, selected, onToggleSelectRow } = props;
  const [model, setModel] = useState<TAttributes>(row);
  const [loading, setLoading] = useState(false);

  const renderCell = (column: IColumn<TAttributes>) => {
    const { dataIndex, actions, render } = column;
    const processAction = async (fn: IActionFn<TAttributes>) => {
      setLoading(true);
      const newModel = await fn(model);
      if (newModel) {
        setModel(newModel);
      }
      setLoading(false);
    };
    if (actions) {
      // render action list
      if (loading) {
        return <CircularProgress size={20} />;
      }
      return (
        <Stack direction="row" spacing={1}>
          {actions
            .filter((a) => !(a.hide && a.hide(model)))
            .map((action) => {
              const { label, onClick } = action;
              return (
                <Link key={label} isAsync onClick={() => processAction(onClick)} text={label} />
              );
            })}
        </Stack>
      );
    } else if (render) {
      // render customised cell
      return render(model);
    } else if (dataIndex) {
      return get(model, dataIndex || '');
    } else {
      return '';
    }
  };

  return (
    <TableRow>
      <TableCell padding="checkbox">
        <Checkbox
          color="primary"
          onClick={onToggleSelectRow}
          checked={selected}
          inputProps={{
            'aria-labelledby': 'enhanced-table-checkbox-#',
          }}
        />
      </TableCell>
      {columns.map((c, index) => (
        <TableCell key={index}>{renderCell(c)}</TableCell>
      ))}
    </TableRow>
  );
}

export default function SelectableResourceTable<TAttributes>(props: IProps<TAttributes>) {
  const { columns, onFetch, defaultQuery = [], filterSchema, defaultSort, batchActions } = props;
  const [filters, setFilters] = useState<IFilters>({});
  const [filtered, setFiltered] = useState(false);
  const [sort, setSort] = useState<{ sortColumn: string; isDesc: boolean } | undefined>(
    defaultSort,
  );

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResourceWithCount<TAttributes>>();
  const [selected, setSelected] = React.useState<string[]>([]);

  const rows = result?.items || [];

  const identifierGetter = (item: TAttributes): string => {
    return get(item, 'id') || '';
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const ids = (result?.items || []).map(identifierGetter);
      setSelected(ids);
      return;
    }
    setSelected([]);
  };

  const handleClickItemCheckbox = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const loadResources = async (pageIndex: number, reset = false) => {
    setLoading(true);
    const filterItems = reset
      ? []
      : Object.keys(filters).map((k) => ({ column: k, value: filters[k] }));
    const options = {
      filters: [...defaultQuery, ...filterItems],
      pageIndex,
      ...(sort || {}),
    };
    try {
      const data = await onFetch(options);
      setResult(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    void loadResources(page - 1);
  }, [page, sort]);

  // Avoid a layout jump when reaching the last page with empty rows.
  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeFilter = (value: string, name: string) => {
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const handleFilter = async () => {
    setLoading(true);

    const hasFilters = Object.values(filters).find((v) => v !== undefined && v !== null);
    if (hasFilters) {
      await loadResources(0);
      setFiltered(true);
    }
    setLoading(false);
  };

  const handleReset = async () => {
    setLoading(true);
    if (filtered) {
      setFilters({});
      setFiltered(false);
      await loadResources(0, true);
    }
    setLoading(false);
  };

  const handleSort = (sortColumn: string) => {
    const previousSortIndex = sort?.sortColumn;
    if (previousSortIndex === sortColumn) {
      setSort({
        sortColumn,
        isDesc: !sort?.isDesc,
      });
    } else {
      setSort({
        sortColumn,
        isDesc: false,
      });
    }
  };

  const renderLoadingRows = () => {
    return (
      <>
        <LinearProgress />
      </>
    );
  };

  const renderTableHead = () => {
    const { sortColumn, isDesc } = sort || {};
    const rowCount = rows.length;
    const numSelected = selected.length;
    return (
      <TableHead>
        <TableRow>
          <StyledTableCell padding="checkbox">
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={handleSelectAllClick}
              inputProps={{
                'aria-label': 'select all desserts',
              }}
            />
          </StyledTableCell>
          {columns.map((c, index) => {
            const { label, sortIndex } = c;
            if (sortIndex) {
              return (
                <StyledTableCell key={index}>
                  <TableSortLabel
                    active={sortColumn === sortIndex}
                    direction={isDesc ? 'desc' : 'asc'}
                    onClick={() => handleSort(sortIndex)}
                  >
                    {label}
                  </TableSortLabel>
                </StyledTableCell>
              );
            }
            return <StyledTableCell key={index}>{label}</StyledTableCell>;
          })}
        </TableRow>
      </TableHead>
    );
  };

  return (
    <Root>
      {filterSchema && (
        <FilterPanel
          fields={filterSchema}
          filtered={filtered}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          onSearch={handleFilter}
          onReset={handleReset}
        />
      )}
      <EnhancedTableToolbar
        selectIds={selected}
        numSelected={selected.length}
        batchActions={batchActions}
      />
      <TableContainer component={Paper}>
        {loading && renderLoadingRows()}

        {!loading && (
          <Table sx={{ minWidth: 500 }} aria-label="custom pagination table">
            {renderTableHead()}
            <TableBody>
              {rows.map((row, index) => {
                const identifier = identifierGetter(row);
                const itemSelected = selected.includes(identifier);
                return (
                  <SingleItem
                    key={index}
                    row={row}
                    columns={columns}
                    selected={itemSelected}
                    onToggleSelectRow={() => handleClickItemCheckbox(identifier)}
                  />
                );
              })}
            </TableBody>
          </Table>
        )}
        {result && !loading && (
          <div className={classes.pagination}>
            <Pagination count={result.pageCount} page={page} onChange={handleChangePage} />
          </div>
        )}
      </TableContainer>
    </Root>
  );
}
